import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { Button } from "../ui";
import { ctaTranslate } from "../lib/ctaTranslate";
import { useLabel } from "../lib/labels";
import { formatWidgetDate } from "../lib/orderLabels";
import { navigateExternal } from "../lib/postmessage";
import { useCouponCopyToast } from "../lib/skuCopyToast";
import type { CouponList } from "../types";

interface Props {
  data: CouponList;
  sessionToken: string;
  /** "list" = 장바구니 적용 쿠폰(coupon_list): Go to basket 없음, 코드복사는 비회원만.
   *  "secret" = 시크릿 쿠폰 발급(secret_coupon): Go to basket 노출.
   *  "myCoupon" = 마이쿠폰(my_coupon_list): 카드 하단 장바구니 이동 버튼(§5),
   *               비로그인 시 로그인하기 버튼(§4·N9). */
  variant?: "list" | "secret" | "myCoupon";
}

/* ─── 시안 매핑 정책 ───────────────────────────────────────────────
 * 시안(ai-lists.html, .ai-gp-coupon--list) 의 단일 .coupon-list 안에 두 가지
 * .coupon-item 변형이 섞여 표시된다:
 *
 *   1) Members Only 쿠폰  → 상단 .coupon-tag, 우측 .btn-link "Go to basket"
 *   2) 일반 쿠폰          → 상단 태그 없음, 우측 .btn-copy "Copy coupon code"
 *
 * 시안엔 두 변형 간 marketing/visual 차이만 있고 모두 highlight 보더 (rainbow)
 * 가 기본이다. 본 컴포넌트는 `membersOnly` 플래그로 두 변형을 구분한다.
 *
 * 미매핑 항목 (시안 정책 A):
 *   - discountValue 배지 / minimumOrder / combinable / 카테고리 분기 그룹화
 *     → 시안 마크업에 슬롯이 없으므로 미노출
 *     → 다만 description 텍스트에 자유롭게 포함될 수 있으므로 backend 측 결정
 * ───────────────────────────────────────────────────────────────── */

/* ─── 쿠폰 상세설명 More/Less 접기 ─────────────────────────────────
 * 시안(ai-lists.html + js/ai-lists.js `btnFold`/`moreList`, AISASSIT-107) 이식.
 *   - .info-detail-wrap: Expires 날짜(고정) + .info-desc-detail(OMS description)
 *   - description 이 임계값(130자) 이상일 때만 .btn-more 노출
 *   - 클릭 시 .info-desc-detail 에 `.on` 토글(3줄 클램프↔펼침) + More↔Less 전환
 * 시안은 mount 시 textContent 길이로 버튼 표시를 결정하지만, React 는 원본
 * description 문자열 길이로 동일 판정한다(동일 소스·동일 임계).
 * ─────────────────────────────────────────────────────────────── */
const COUPON_DESC_FOLD_THRESHOLD = 130; // 시안 ai-lists.js infoDetailLength

function CouponDesc({ expiryLine, description }: { expiryLine: string; description?: string }) {
  const [open, setOpen] = useState(false);
  const moreLabel = useLabel("more", "More"); // 시안 data-fold-open
  const lessLabel = useLabel("less", "Less"); // 시안 data-fold-close
  const detail = (description ?? "").trim();
  const showFold = detail.length >= COUPON_DESC_FOLD_THRESHOLD;

  return (
    <div className="info-desc">
      <div className="info-detail-wrap">
        <p>{expiryLine}</p>
        {detail ? (
          <p className={`info-desc-detail${open ? " on" : ""}`} aria-expanded={open}>
            {detail}
          </p>
        ) : null}
      </div>
      {showFold ? (
        <button
          type="button"
          className={`btn-more${open ? " active" : ""}`}
          onClick={() => setOpen((v) => !v)}
        >
          <span>{open ? lessLabel : moreLabel}</span>
        </button>
      ) : null}
    </div>
  );
}

/* ─── main component ─── */

export default function CouponListCard({ data, sessionToken, variant = "list" }: Props) {
  const isSecret = variant === "secret";
  const isMyCoupon = variant === "myCoupon";
  const coupons = data.coupons ?? [];
  const { copyCode } = useCouponCopyToast();
  const languageCode = data.languageCode || "en";

  // display 표기언어 i18n 라벨(단일 경로 — labels.tsx). 미주입 시 영어 폴백.
  const goToBasketLabel = useLabel("goToBasket", "Go to basket");
  const signInLabel = useLabel("signIn", "Sign in");
  const noCouponsLabel = useLabel("noCoupons", "No coupons available.");
  const expiresTodayLabel = useLabel("expiresToday", "Expires today");
  const oneDayLeftLabel = useLabel("onlyOneDayLeft", "Only 1 day left on this voucher");
  const nDaysLeftTpl = useLabel("onlyNDaysLeft", "Only {n} days left on this voucher");
  const codeLineTpl = useLabel("codeLine", "Code: {code}");
  const expiryDateLineTpl = useLabel("expiryDateLine", "Expires {date}");
  // 마이쿠폰 헤더(my_coupon.title) — labels 주입 시 번역, 미주입 시 서버 data.title.
  const titleLabel = useLabel("title", data.title || "");

  const formatDaysLeftLabel = (daysLeft: number): string => {
    // §3-1 임박 문구(validity pill). (3-2 카드 강조는 0621 6/9 삭제 — pill 텍스트만 유지)
    if (daysLeft <= 0) return expiresTodayLabel;
    if (daysLeft === 1) return oneDayLeftLabel;
    return nDaysLeftTpl.replace("{n}", String(daysLeft));
  };

  const formatExpiryLine = (iso: string): string => {
    const formatted = formatWidgetDate(iso, languageCode, "long");
    return expiryDateLineTpl.replace("{date}", formatted);
  };

  // 정책 3.4.4 유효기간 상태 — 만료일 기준 D-day Countdown.
  //   >30일 남음 또는 만료일 미설정 → 상태 없음(validity pill 미노출)
  //   30~11일 → 일반("N days left"), 10일 이하 → 임박("N days left")
  // (백엔드가 daysLeft 를 채우면 그 값 사용, 없으면 expiresAt 로 계산)
  const daysLeftOf = (c: (typeof coupons)[number]): number | null => {
    if (typeof c.daysLeft === "number") return c.daysLeft;
    if (!c.expiresAt) return null;
    const d = new Date(c.expiresAt);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((d.getTime() - today.getTime()) / 86_400_000);
  };

  const handleCopy = (code: string) => {
    copyCode(code);
  };

  const handleGoToBasket = (coupon: (typeof coupons)[number]) => {
    if (!data.cartUrl) {
      console.warn("[CouponListCard] data.cartUrl 누락 — 서버가 build_nav_url 로 채워야 함");
      return;
    }
    navigateExternal(
      data.cartUrl,
      { target: "_top", code: coupon.code },
      { sessionId: sessionToken },
    );
    void ctaTranslate({
      code: "coupon_list.apply_coupon_and_show_me_the_cart",
      params: { code: coupon.code },
      sessionToken,
      fallback: `Apply coupon ${coupon.code} and show me the cart`,
      toolCall: {
        tool: "commerce_apply_coupon",
        args: { code: coupon.code },
      },
    });
  };

  // §4·N9 — 마이쿠폰 비로그인: '로그인하기' 버튼(닷컴 로그인 딥링크) 노출.
  const handleLogin = () => {
    if (!data.loginUrl) {
      console.warn("[CouponListCard] data.loginUrl 누락 — 서버가 build_nav_url 로 채워야 함");
      return;
    }
    navigateExternal(data.loginUrl, { target: "_top" }, { sessionId: sessionToken });
  };

  // §5 마이쿠폰 '장바구니로 이동'은 호스트 linkout 칩으로 이동(AISASSIT-66 my_coupon 확장).
  // 카드 내 핸들러 불필요 — render.py 가 next_action linkout 슬롯으로 emit.

  if (isMyCoupon && data.loginRequired) {
    // §4·N9 비회원 — 헤딩 없이 Sign in 버튼만. Figma/ai-lists 구매하기 화면과 동일한
    // .ai-gp1-btn-wrap(콘텐츠 폭·좌측정렬) + DS primary/xs(빨강) 버튼 (AISASSIT-114 재오픈).
    return (
      <div className="ai-gp1--section">
        <div className="ai-gp1-btn-wrap">
          <Button
            variant="primary"
            size="sm"
            onClick={handleLogin}
          >
            {signInLabel}
          </Button>
        </div>
      </div>
    );
  }

  if (coupons.length === 0) {
    return (
      <div className="ai-gp1--section">
        <div className="ai-gp-coupon--list">
          <p style={{ padding: "16px", color: "#646464" }}>
            {noCouponsLabel}
          </p>
        </div>
      </div>
    );
  }

  // 보유 4건 이상 → 가로 스와이프 캐러셀(한 페이지 3개씩 세로 스택) + 인디케이터. 3건 이하는 세로 목록(인디케이터 X).
  const showCarousel = coupons.length >= 4;
  const pages: (typeof coupons)[] = [];
  if (showCarousel) {
    for (let i = 0; i < coupons.length; i += 3) pages.push(coupons.slice(i, i + 3));
  }

  const renderCoupon = (c: (typeof coupons)[number]) => {
    const isMembersOnly = !!c.membersOnly;
    // 정책 3.4.4: 30일 이하 남았을 때만 validity pill 노출(>30일/미설정 → 없음).
    const days = daysLeftOf(c);
    const showValidity = days !== null && days <= 30;
    return (
      <div key={c.code} className="coupon-item">
        <div className="coupon-item--inner">
          <div className="coupon-info">
            <div className="info-inner">
              <div className="info-title">{c.name}</div>
              {/* validity pill (Figma 2109:9397): 시계 아이콘 + "N days left" #ea1917 */}
              {showValidity && (
                <div className="info-expire-date">
                  <span className="info-expire-date__icon" aria-hidden="true" />
                  <span>{formatDaysLeftLabel(days as number)}</span>
                </div>
              )}
              {/* 본문(info-desc): Expires 날짜 + OMS coupon description(More/Less 접기). Code 는 CTA 로 이동. */}
              <CouponDesc expiryLine={formatExpiryLine(c.expiresAt)} description={c.description} />
            </div>
          </div>
          {/* CTA: secret_coupon=Go to basket / coupon_list 비회원=Code 복사 / 회원=없음 */}
          {/* isSecret ? (
            <div className="coupon-btn">
              <a
                className="btn-link"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleGoToBasket(c);
                }}
              >
                <span>{goToBasketLabel}</span>
              </a>
            </div>
          ) :*/ !isMembersOnly ? (
            <div className="coupon-btn">
              <button
                type="button"
                className="btn-copy"
                onClick={() => handleCopy(c.code)}
              >
                <span>{codeLineTpl.replace("{code}", c.code)}</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="ai-gp1--section">
      <div className={`ai-gp-coupon--list${isSecret ? " ai-gp-coupon--secret" : ""}`}>
        {/* 마이쿠폰은 카드 상단 타이틀 미노출(AISASSIT: My Coupon 타이틀 삭제 재적용). */}
        {data.title && !isMyCoupon ? <h3 className="coupon-list__title">{titleLabel}</h3> : null}
        {showCarousel ? (
          <Swiper
            className="coupon-swiper swiper"
            modules={[Pagination]}
            pagination={{ clickable: true }}
            spaceBetween={12}
            slidesPerView={1}
            watchOverflow={true}
            updateOnWindowResize={true}
          >
            {pages.map((page, i) => (
              <SwiperSlide key={i}>
                <div className="coupon-list">{page.map(renderCoupon)}</div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="coupon-list">{coupons.map(renderCoupon)}</div>
        )}
        {/* §5 — 마이쿠폰 '장바구니로 이동'은 카드 내 버튼 대신 호스트 linkout 칩으로 노출.
            (AISASSIT-66 my_coupon 확장: render.py my_coupon RenderSpec linkout_basket=True →
             next_action 칩 emit. 카드 내 btn-go-basket 제거.) */}
      </div>
    </div>
  );
}
